import React, { useEffect, memo, CSSProperties } from 'react'
import { createRoot } from 'react-dom/client'

import Cross from './icons/cross.inline.svg'
import styles from './toast.module.scss'

interface ToastProps {
  message: string
  type?: 'success' | 'error' | 'info' | 'warning'
  duration?: number
  onClose?: () => void
}

export interface ToastOptions extends Pick<ToastProps, 'type' | 'message' | 'duration'> {
  showToast: boolean
}

const Toast = memo(({ message, type = 'info', duration = 3000, onClose }: ToastProps) => {
  const closeDuration = duration + 1000
  useEffect(() => {
    const toastTimeout = setTimeout(() => {
      onClose?.()
    }, closeDuration)

    return () => {
      clearTimeout(toastTimeout)
    }
  }, [closeDuration, onClose])

  const toastStyle = {
    '--fade-out-delay': `${duration / 1000}s`,
  } as CSSProperties

  return (
    <div className={`${styles.toast} ${styles[type]}`} style={toastStyle}>
      <p>{message}</p>
      <Cross
        className={styles.iconContainer}
        onClick={onClose}
        // @i18n-ignore
        role="button"
      />
    </div>
  )
})

Toast.displayName = 'Toast'

let toastContainer: HTMLElement | null = null
const getToastContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement('div')
    toastContainer.id = 'toast-container'
    Object.assign(toastContainer.style, {
      // @i18n-ignore
      position: 'fixed',
      // @i18n-ignore
      bottom: '1.5rem',
      zIndex: '9999',
      // @i18n-ignore
      display: 'flex',
      // @i18n-ignore
      flexDirection: 'column',
      // @i18n-ignore
      gap: '10px',
      // @i18n-ignore
      padding: '1rem',
    })
    document.body.appendChild(toastContainer)
  }
  return toastContainer
}

export const showToast = ({ message, type = 'info', duration = 3000 }: ToastProps) => {
  const container = getToastContainer()
  const toastElement = document.createElement('div')
  container.appendChild(toastElement)

  const root = createRoot(toastElement)

  const cleanup = () => {
    root.unmount()
    container.removeChild(toastElement)
    if (container.childElementCount === 0) {
      document.body.removeChild(container)
      toastContainer = null
    }
  }

  root.render(<Toast message={message} type={type} duration={duration} onClose={cleanup} />)
}

export default Toast
